import { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Mic from '@mui/icons-material/esm/Mic';
import Stop from '@mui/icons-material/esm/Stop';

interface SpeechRecognitionEventLike extends Event {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEventLike extends Event {
  error?: string;
}

interface SpeechRecognitionLike extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

interface SpeechWindow extends Window {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
}

interface VoiceInputProps {
  onText: (text: string) => void;
  disabled?: boolean;
  label?: string;
}

export default function VoiceInput({ onText, disabled = false, label = 'Dictate' }: VoiceInputProps) {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [listening, setListening] = useState(false);
  const [failed, setFailed] = useState(false);

  const Recognition = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const speechWindow = window as SpeechWindow;
    return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
  }, []);

  useEffect(() => () => {
    recognitionRef.current?.abort();
  }, []);

  if (!Recognition || failed) return null;

  const stop = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  const start = () => {
    if (disabled) return;
    const recognition = new Recognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript ?? '')
        .join(' ')
        .trim();
      if (transcript) onText(transcript);
    };
    recognition.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setFailed(true);
      }
      setListening(false);
    };
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch {
      recognitionRef.current = null;
      setListening(false);
    }
  };

  return (
    <Tooltip title={listening ? 'Stop dictation' : label}>
      <span>
        <Button
          size="small"
          variant={listening ? 'contained' : 'outlined'}
          color={listening ? 'error' : 'primary'}
          startIcon={listening ? <Stop /> : <Mic />}
          onClick={listening ? stop : start}
          disabled={disabled}
          aria-pressed={listening}
        >
          {listening ? 'Stop' : label}
        </Button>
      </span>
    </Tooltip>
  );
}
